import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createComposer, getComposerByID, editComposer } from '../../store/actions/composer/composer.action';
import CreateComposer from '../../components/Composers/CreateComposer';
import PageLoader from '../../components/Common/PageLoader';

const CreateComposersContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.composer.isCreating);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [composerToBeEdit, setComposerToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const composerCategory = categories.find((category) => category.link === 'composers');
      if (composerCategory) {
        setAccessMode(composerCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Composer category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch composer for edit mode
  const getComposer = useCallback(async (composerID) => {
    try {
      const composer = await dispatch(getComposerByID(composerID));
      setComposerToBeEdit(composer);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching composer:', error);
      navigate('/composers');
    }
  }, [dispatch, navigate]);

  // Handle create composer
  const handleCreateComposer = useCallback((composerData) => {
    return dispatch(createComposer(composerData));
  }, [dispatch]);

  // Handle edit composer
  const handleEditComposer = useCallback((composerData) => {
    return dispatch(editComposer(composerData));
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    // For URL: /composer/edit/15f23d44-ac79-4d02-ae5c-6b7f867f788c
    // params will be: { mode: 'edit', id: '15f23d44-ac79-4d02-ae5c-6b7f867f788c' }

    const currentMode = params?.mode === 'edit' ? 'EDIT' : 'CREATE';
    setMode(currentMode);

    if (params?.id && currentMode === 'EDIT') {
      getComposer(params.id);
    } else {
      setIsLoading(false);
    }

    setAccess();
  }, [params, getComposer, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (isLoading) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <CreateComposer
        accessMode={accessMode}
        onCreate={handleCreateComposer}
        onEdit={handleEditComposer}
        initialValues={composerToBeEdit}
        mode={mode}
        isCreating={isCreating}
      />
    </section>
  );
};

export default CreateComposersContainer;
